export const CONTACT = {
  emailInfo: "info@foxconsultancy.org",
  emailCarlo: "carlo@foxconsultancy.org",
  phone: "+44 (0)7827 927406",
  phoneHref: "tel:+447827927406",
  web: "www.foxconsultancy.org",
  webHref: "https://www.foxconsultancy.org",
};

export const MARQUEE_ITEMS = [
  "Project Management",
  "Employer's Agent",
  "Project Monitoring",
  "Regeneration",
  "Build to Rent",
  "Arenas & Event Spaces",
  "Healthcare",
  "Infrastructure",
  "Utilities",
];

export const PILLARS = [
  {
    num: "01",
    id: "agile",
    title: "Agile",
    text: "We don't force every engagement into a rigid template. Our approach flexes with shifting priorities, evolving scope and constraints — adjusting plans quickly without losing sight of the goal.",
    benefit:
      "Fewer delays when circumstances change, faster course-correction when risks emerge, and a delivery partner who adapts to your business rather than asking you to adapt to ours.",
  },
  {
    num: "02",
    id: "smart",
    title: "Smart",
    text: "Decisions backed by data, experience and judgment. We combine proven frameworks with sharp, situational thinking — knowing when to follow the process and when to make a judgment call. Resources, time and budget are managed with precision, not guesswork.",
    benefit:
      "Confidence that decisions are deliberate, risk is managed proactively, and your investment is being handled with rigour.",
  },
  {
    num: "03",
    id: "adaptive",
    title: "Adaptive",
    text: "Every project is different. No two organisations, teams or challenges are the same, so we tailor our methods, tools and communication style to fit the client, the industry and the complexity of the project.",
    benefit:
      "A partner that fits into how you already work, rather than disrupting it — reducing friction and accelerating buy-in across your team.",
  },
];

export const SERVICES = [
  {
    num: "01",
    id: "project-management",
    title: "Project Management",
    short: "End-to-end leadership of construction projects from inception to handover.",
    text: "We hold programme, cost and quality, chair the right conversations, and make sure every party knows what good looks like.",
    points: [
      "Full lifecycle leadership — inception to handover",
      "Programme, cost and quality control",
      "Design team and contractor coordination",
      "Stakeholder and risk management",
      "Planning consents and technical approvals",
    ],
  },
  {
    num: "02",
    id: "employers-agent",
    title: "Employer's Agent",
    short: "Acting on your behalf during construction, managing the contract end to end.",
    text: "We identify and resolve issues early so they don't become problems later. We stay in the detail and keep the project aligned with your specification and brief.",
    points: [
      "Contract administration (JCT / NEC)",
      "Early issue identification and resolution",
      "Valuation and payment certification",
      "Change control and cost reporting",
      "Delivery kept aligned to your brief",
    ],
  },
  {
    num: "03",
    id: "project-monitoring",
    title: "Project Monitoring",
    short: "Your eyes and ears on a development — protecting budget, programme and stakeholder interests.",
    text: "As project monitors we keep track of the development, protect budget, programme and stakeholder interests, and ensure delivery of a quality project.",
    points: [
      "Independent funder and lender monitoring",
      "Budget and programme protection",
      "Quality and compliance auditing",
      "Risk reporting and drawdown reviews",
      "Stakeholder interest representation",
    ],
  },
];

export const SECTORS = [
  { name: "Residential", text: "Build to rent, affordable, mid-market and private sale housing at every density." },
  { name: "Commercial", text: "Workplaces, retail and mixed-use schemes delivered with commercial rigour." },
  { name: "Event Spaces", text: "Arenas and venues built for hundreds of thousands of visitors a year." },
  { name: "Infrastructure", text: "Civil infrastructure, new towns and strategic land enabling works." },
  { name: "Utilities", text: "Net-zero energy solutions and utility coordination across complex sites." },
];

export const PROCESS = [
  { num: "01", title: "Discovery", text: "Understanding your brief, constraints and ambitions." },
  { num: "02", title: "Audit", text: "Reviewing risk, programme, cost and governance." },
  { num: "03", title: "Strategy", text: "A tailored delivery plan built around your project." },
  { num: "04", title: "Execution", text: "Hands-on leadership through design and construction." },
  { num: "05", title: "Handover", text: "Certainty at completion — and beyond." },
];

export const PROJECT_FILTERS = [
  { id: "all", label: "All Projects", testid: "portfolio-filter-all" },
  { id: "regeneration", label: "Regeneration", testid: "portfolio-filter-regeneration" },
  { id: "residential", label: "Residential / BTR", testid: "portfolio-filter-residential" },
  { id: "healthcare", label: "Healthcare & Public", testid: "portfolio-filter-healthcare" },
  { id: "sports", label: "Sports & Hospitality", testid: "portfolio-filter-sports" },
];

export const PROJECTS = [
  {
    id: "fountainbridge-masterplan",
    title: "Fountainbridge Masterplan",
    client: "Cruden / Buccleuch",
    location: "Edinburgh",
    value: "£300M",
    status: "Procurement",
    date: "2022 — Present",
    filter: "regeneration",
    category: "Regeneration & Masterplanning",
    summary: "A new canalside neighbourhood — one of Scotland's largest bespoke rental developments.",
    body: "Regeneration of the canal area, delivering 464 new homes within a new mixed-use development. The residential buildings are designed for rent, comprising a mix of private rental and tenure-blind affordable homes. The proposals include commercial and retail units, a mixed-use hotel development and a placemaking approach that makes residents feel part of the design. A pedestrian-friendly environment with an active frontage of cafes, restaurants and shops will reinvigorate the canalside — creating a vibrant new place where there has been a void in the city for many years.",
    highlights: [
      "464 new canalside homes",
      "Mixed-use hotel, retail and commercial units",
      "Active frontage of cafes, restaurants and shops",
      "Pedestrian-friendly placemaking on the Union Canal",
    ],
    image: "/assets/projects/fountainbridge-masterplan-1.jpg",
    gallery: ["/assets/projects/fountainbridge-masterplan-2.jpg", "/assets/projects/fountainbridge-colonies.jpg"],
  },
  {
    id: "edinburgh-park-arena",
    title: "Edinburgh Park Arena",
    client: "AEG Europe",
    location: "Edinburgh",
    value: "Confidential",
    status: "Procurement & Naming Rights",
    date: "2025 — 2026",
    filter: "sports",
    category: "Arena & Event Space",
    summary: "Edinburgh's first large indoor arena — 8,500 capacity and 18,500 sqm of venue space.",
    body: "AEG Europe — which owns London's The O2 Arena, Berlin's Uber Arena and 325 venues across the world — is working with award-winning developers on the new arena, which will attract an estimated 700,000 visitors per year. The arena hopes to host 150 shows a year including concerts, comedy, boxing and festival shows, located on the doorstep of the Edinburgh Park train and tram stations.",
    highlights: [
      "8,500-capacity indoor arena — Edinburgh's first",
      "18,500 sqm venue beside Edinburgh Park stations",
      "Around 700,000 visitors and 150 shows per year",
      "Delivered with AEG Europe, owner of The O2",
    ],
    image: "/assets/projects/edinburgh-park-arena-1.jpg",
    gallery: [
      "/assets/projects/edinburgh-park-arena-2.jpg",
      "/assets/projects/edinburgh-park-arena-3.jpg",
    ],
  },
  {
    id: "fountainbridge-build-to-rent",
    title: "Fountainbridge Build to Rent",
    client: "Cruden / Buccleuch",
    location: "Edinburgh",
    value: "£100M+",
    status: "Design",
    date: "2022 — Present",
    filter: "residential",
    category: "Build to Rent",
    summary: "278 studio to three-bed apartments across two residential blocks with a Net Zero Carbon heating solution.",
    body: "These new homes will be delivered over two large residential blocks with undercroft car parking. The development is a tenure-blind design with a focus on highly sustainable construction materials and a Net Zero Carbon heating solution via energy-efficient air source heat pumps providing ventilation and heating. Alongside the residential element, the site will include shops, social enterprise and community spaces with high-quality public realm — largely car-free and integrated into the wider Fountainbridge masterplan.",
    highlights: [
      "278 studio to three-bed apartments",
      "Two residential blocks with undercroft parking",
      "Net Zero Carbon heating via air source heat pumps",
      "Largely car-free with shops and community space",
    ],
    image: "/assets/projects/fountainbridge-btr-1.jpg",
    gallery: ["/assets/projects/fountainbridge-btr-2.jpg", "/assets/projects/fountainbridge-colonies.jpg"],
  },
  {
    id: "fountainbridge-affordable-mmr",
    title: "Fountainbridge Affordable & MMR",
    client: "City of Edinburgh Council",
    location: "Edinburgh",
    value: "£50M+",
    status: "Procurement",
    date: "2022 — 2026",
    filter: "residential",
    category: "Affordable Housing",
    summary: "186 affordable and mid-market rent homes beside the Union Canal.",
    body: "A mixed-tenure scheme comprising affordable housing — including social rent and mid-market rent — alongside private sale or rental units, providing at least 10% accessible homes. Designed as a largely car-free, pedestrian-friendly environment targeting net-zero carbon energy and sustainable heating. Commercial and public space includes approximately 5,000 sq ft of mixed-use retail, active frontages with cafes and shops, and a large communal civic and growing space adjacent to the Union Canal.",
    highlights: [
      "186 affordable and mid-market rent homes",
      "At least 10% accessible homes",
      "Around 5,000 sq ft of retail and commercial frontage",
      "Communal civic and growing space by the Union Canal",
    ],
    image: "/assets/projects/fountainbridge-affordable-mmr-1.jpg",
    gallery: [
      "/assets/projects/fountainbridge-affordable-mmr-2.jpg",
      "/assets/projects/fountainbridge-affordable-mmr-3.jpg",
    ],
  },
  {
    id: "herdmanflat-redevelopment",
    title: "Herdmanflat Redevelopment",
    client: "East Lothian Council",
    location: "Haddington, East Lothian",
    value: "£15M",
    status: "In Construction",
    date: "2024 — 2025",
    filter: "residential",
    category: "Age-Friendly Housing",
    summary: "51 age-friendly homes on the former Herdmanflat hospital site.",
    body: "The Herdmanflat redevelopment includes the construction of new homes and the conversion of existing buildings in later phases. The first phase — approved by Planning Committee in May 2025 — sits in the southern section of the site and features four three-storey buildings and one two-storey building accommodating 51 homes. The units are designed for an ageing population, including dementia-friendly design; all 51 homes are accessible to wheelchair users if ever required and easily adaptable to suit individual tenant needs.",
    highlights: [
      "51 age-friendly, dementia-friendly homes",
      "All homes wheelchair adaptable",
      "Four three-storey and one two-storey buildings",
      "Phase one approved May 2025",
    ],
    image: "/assets/projects/herdmanflat-redevelopment-1.jpg",
    gallery: ["/assets/projects/herdmanflat-redevelopment-2.jpg", "/assets/projects/herdmanflat-redevelopment-3.jpg"],
  },
  {
    id: "waterbeach-new-town",
    title: "Waterbeach New Town",
    client: "Urban&Civic / DIO",
    location: "Cambridge",
    value: "Confidential",
    status: "In Construction",
    date: "2016 — 2022",
    filter: "regeneration",
    category: "New Town & Infrastructure",
    summary: "A 6,500-home sustainable community on a 716-acre former barracks and airfield.",
    body: "Waterbeach Barracks was used by the RAF and the Royal Engineers from 1966 to 2013. The redevelopment is a major 6,500-home sustainable community six miles north of Cambridge on brownfield land. The first phase delivers 1,600 homes — from apartments to five-bedroom houses — across sale, shared ownership and rent, alongside schools, a town centre and extensive green space. Sustainability leads the design: over 98% of hard materials on site recycled, air source heat pumps and EV charging throughout.",
    highlights: [
      "6,500-home sustainable new town",
      "716-acre former barracks and airfield",
      "Phase one: 1,600 homes plus schools and town centre",
      "Over 98% of hard materials recycled on site",
    ],
    image: "/assets/projects/waterbeach-new-town-1.jpg",
    gallery: ["/assets/projects/waterbeach-new-town-2.jpg", "/assets/projects/waterbeach-new-town-3.jpg"],
  },
  {
    id: "royal-papworth-student-accommodation",
    title: "Royal Papworth Student Accommodation",
    client: "Royal Papworth Hospital",
    location: "Cambridge",
    value: "£5M",
    status: "Complete",
    date: "2017 — 2019",
    filter: "healthcare",
    category: "Healthcare Accommodation",
    summary: "Over 230 single and en-suite bedrooms for hospital staff, converted from military barrack blocks.",
    body: "Refurbishment of existing military barrack blocks into accommodation for students of the hospital. Works included the strip-out of two large barrack blocks and construction of new single and en-suite bedrooms for staff of Royal Papworth Hospital, with large shared communal areas including lounges, kitchens and dining rooms. The accommodation includes free laundry facilities, free WiFi and free residents' parking.",
    highlights: [
      "230+ single and en-suite bedrooms",
      "Conversion of two military barrack blocks",
      "Communal lounges, kitchens and dining rooms",
      "Free laundry, WiFi and residents' parking",
    ],
    image: "/assets/projects/royal-papworth-student-accommodation-1.jpg",
    gallery: [
      "/assets/projects/royal-papworth-student-accommodation-2.jpg",
      "/assets/projects/royal-papworth-student-accommodation-3.jpg",
    ],
  },
  {
    id: "hampton-by-hilton-stansted",
    title: "Hampton by Hilton, Stansted Airport",
    client: "Hilton",
    location: "Stansted Airport",
    value: "£38M",
    status: "Complete",
    date: "2016 — 2018",
    filter: "sports",
    category: "Hotel & Hospitality",
    summary: "The largest Hampton by Hilton in Europe — 357 rooms linked directly to the terminal.",
    body: "The 357-bed, eight-storey hotel is directly linked to the terminal via a covered walkway, giving passengers seamless landside access, and was designed to support future expansion of the airport's main terminal. Delivered by contractor McAleer & Rushe, it is one of the largest Hampton by Hilton hotels in the brand's portfolio, with many rooms overlooking the runway. Bordering the airfield, the site demanded full airside security and safety compliance throughout construction — restricted access, vetted personnel and close coordination with airport operations around continuous operating hours.",
    highlights: [
      "357 rooms over eight storeys",
      "Largest Hampton by Hilton in Europe",
      "Covered walkway direct to the terminal",
      "Full airside security compliance during construction",
    ],
    image: "/assets/projects/hampton-by-hilton-stansted-1.jpg",
    gallery: ["/assets/projects/hampton-by-hilton-stansted-2.jpg", "/assets/projects/hampton-by-hilton-stansted-3.jpg"],
  },
];

export const CLIENTS = [
  "Cruden",
  "Buccleuch",
  "AEG Europe",
  "City of Edinburgh Council",
  "Urban&Civic",
  "Defence Infrastructure Organisation",
  "Loch Lomond & The Trossachs NP",
  "Cannon Kirk",
  "Hilton",
  "East Lothian Council",
  "Royal Papworth Hospital",
];

export const QUALIFICATIONS = [
  { abbr: "MCIOB", name: "Chartered Construction Manager", year: "2020", testid: "qualification-badge-mciob" },
  { abbr: "MAPM", name: "Project Management", year: "2019", testid: "qualification-badge-mapm" },
  { abbr: "PMP", name: "Project Management Professional", year: "2014", testid: "qualification-badge-pmp" },
  { abbr: "BEng", name: "Civil Engineering", year: "2014", testid: "qualification-badge-beng" },
];

export const MILESTONES = [
  {
    title: "Two decades across the sector",
    text: "Director and management roles with consultancies, private developers and public bodies — a career spanning infrastructure, residential, mixed-use and commercial delivery.",
  },
  {
    title: "£300M development leadership",
    text: "Led developments valued at up to £300 million, with strong commercial acumen and a consistent focus on value for money.",
  },
  {
    title: "Consents and approvals",
    text: "A proven track record of securing planning consents and technical approvals, and managing procurement on complex, multi-stakeholder schemes.",
  },
  {
    title: "Founding Fox",
    text: "Founded Fox Project Management to bring leadership, governance and stakeholder management expertise directly to clients — hands-on, on every instruction.",
  },
];

export const FAQS = [
  {
    q: "What types of project does Fox take on?",
    a: "We work across residential, commercial, event spaces, infrastructure and utilities — from £5M refurbishments to £300M masterplans. If certainty of delivery matters to you, we want to hear about it.",
  },
  {
    q: "Where do you operate?",
    a: "Across the UK and Ireland. Current and recent schemes span Edinburgh, East Lothian, Cambridge and London Stansted.",
  },
  {
    q: "Who will I actually work with?",
    a: "Every instruction is director-led. You deal directly with Carlo from first conversation to handover — no hand-offs to junior teams.",
  },
  {
    q: "How are fees structured?",
    a: "Every engagement is scoped individually. Get in touch with the outline of your project and we will come back with a tailored proposal.",
  },
];
